import {
  GAR,
  LaboratoryResearch,
  Analysis as OpenApiAnalysis,
  AnalysisProcess as OpenApiAnalysisProcess,
  FieldMeasurement as OpenApiFieldMeasurement,
  UploadTask,
} from "@nens/brostar/openapi-client";
import {
  EditGARTask,
  GARTask,
  indicationYesNoOptionsType,
  pumpTypeOptionsType,
  qualityControlMethodOptionsType,
  qualityRegimeOptionsType,
  samplingStandardOptionsType,
  statusQualityControlOptionsType,
} from "@nens/brostar/uploadapi-client";

import { isValidEnum } from "./is-valid-enum";

export type GarLabData = {
  labResearch: LaboratoryResearch;
  processes: { process: OpenApiAnalysisProcess; analyses: OpenApiAnalysis[] }[];
}[];

// UPLOAD TASK
export const convertGARUploadTaskToCreateTask = ({
  projectNumber,
  metadata,
  sourcedocumentData,
}: UploadTask): GARTask => {
  const { qualityRegime, deliveryAccountableParty } = metadata as { [key: string]: string };
  const { qualityControlMethod } = sourcedocumentData as { [key: string]: string };
  const { groundwaterMonitoringNets } = sourcedocumentData as { [key: string]: string[] };

  return {
    metadata: {
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      samplingOperator: "",
      samplingStandard: isValidEnum(samplingStandardOptionsType, "onbekend"), // Bemonsteringsprocedure
      qualityControlMethod: isValidEnum(qualityControlMethodOptionsType, qualityControlMethod), // Beoordelingsprocedure
      responsibleLaboratoryKvk: "", // kvk nummer
      groundwaterMonitoringNets: groundwaterMonitoringNets, // gmn id's
      deliveryAccountableParty: deliveryAccountableParty ?? "", // kvk nummer
    },
    projectNumber: projectNumber,
    fieldworkFile: new Blob(),
    labFile: new Blob(),
  };
};

// EDIT (from existing GAR object)
export const convertGARToEditTask = (
  {
    broId,
    qualityRegime,
    deliveryAccountableParty,
    qualityControlMethod,
    gmwBroId,
    tubeNumber,
    samplingDatetime,
    samplingStandard,
    pumpType,
    abnormalityInCooling,
    abnormalityInDevice,
    pollutedByEngine,
    filterAerated,
    groundwaterLevelDroppedTooMuch,
    abnormalFilter,
    sampleAerated,
    hoseReused,
    temperatureDifficultToMeasure,
  }: GAR,
  fieldMeasurements: OpenApiFieldMeasurement[] = [],
  labData: GarLabData = []
): EditGARTask => {
  return {
    metadata: {
      broId: broId,
      projectNumber: "",
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      deliveryAccountableParty: deliveryAccountableParty ?? "",
      correctionReason: "eigenCorrectie",
    },
    sourcedocumentData: {
      objectIdAccountableParty: "",
      gmwBroId: gmwBroId ?? "",
      tubeNumber: tubeNumber ?? "",
      qualityControlMethod: isValidEnum(qualityControlMethodOptionsType, qualityControlMethod),
      fieldResearch: {
        samplingDateTime: samplingDatetime?.toISOString() ?? "",
        samplingStandard: isValidEnum(samplingStandardOptionsType, samplingStandard) ?? "",
        pumpType: isValidEnum(pumpTypeOptionsType, pumpType),
        abnormalityInCooling: isValidEnum(indicationYesNoOptionsType, abnormalityInCooling),
        abnormalityInDevice: isValidEnum(indicationYesNoOptionsType, abnormalityInDevice),
        pollutedByEngine: isValidEnum(indicationYesNoOptionsType, pollutedByEngine),
        filterAerated: isValidEnum(indicationYesNoOptionsType, filterAerated),
        groundWaterLevelDroppedTooMuch: isValidEnum(
          indicationYesNoOptionsType,
          groundwaterLevelDroppedTooMuch
        ),
        abnormalFilter: isValidEnum(indicationYesNoOptionsType, abnormalFilter),
        sampleAerated: isValidEnum(indicationYesNoOptionsType, sampleAerated),
        hoseReused: isValidEnum(indicationYesNoOptionsType, hoseReused),
        temperatureDifficultToMeasure: isValidEnum(
          indicationYesNoOptionsType,
          temperatureDifficultToMeasure
        ),
        fieldMeasurements: fieldMeasurements.map((fm) => ({
          parameter: fm.parameter,
          unit: fm.unit ?? "",
          fieldMeasurementValue: parseFloat(fm.fieldMeasurementValue ?? "0"),
          qualityControlStatus: isValidEnum(
            statusQualityControlOptionsType,
            fm.qualityControlStatus
          ),
        })),
      },
      laboratoryAnalyses: labData.map(({ labResearch, processes }) => ({
        responsibleLaboratoryKvk: labResearch.laboratoryKvkNumber ?? undefined,
        analysisProcesses: processes.map(({ process, analyses }) => ({
          date: process.analysesDate.toISOString().split("T")[0],
          analyticalTechnique: process.analyticalTechnique ?? undefined,
          validationMethod: process.validationMethod ?? undefined,
          analyses: analyses.map((a) => ({
            parameter: a.parameter,
            unit: a.unit ?? "",
            analysisMeasurementValue: a.value != null ? parseFloat(a.value) : undefined,
            limitSymbol: a.limitSymbol ?? undefined,
            reportingLimit: a.reportingLimit != null ? parseFloat(a.reportingLimit) : undefined,
            qualityControlStatus: a.statusQualityControl ?? "",
          })),
        })),
      })),
    },
  };
};
